import React, { useCallback, useEffect, useState } from 'react';
import { Card, Col, Flex, Image, message, Row } from 'antd';
import { CustomTitle } from '../../../components';
import { Link, useLocation } from 'react-router-dom';
import CustomsContact from '../components/CustomsContact';

// Assets
import AA from '../../../assets/images/Airports/airline-img1.png';
import DL from '../../../assets/images/Airports/airline-img2.png';
import WN from '../../../assets/images/Airports/airline-img3.png';
import UA from '../../../assets/images/Airports/airline-img4.png';
import LH from '../../../assets/images/Airports/luftansa.png';
import AF from '../../../assets/images/Airports/KLM.png';

import Breadcrumbs from '../../../components/Breadcrumbs';
import { getAllAirlines, Airport } from '@/apis/airports';
import { useUnAuthorize } from '@/hooks';
import { useSpin } from '@/components/SpinContext';

interface EnhancedAirline extends Airport {
  img: string | null;
}

interface LocationState {
  airport?: Airport;
}

const airlineImageMap: Record<string, string> = {
  AA,
  DL,
  WN,
  UA,
  LH,
  AF,
};

// Logos drawn in dark ink on a transparent background; flipped in dark mode.
const darkInkLogos = new Set(['LH']);

/**
 * AirlineList Component
 *
 * Displays a list of available airlines for a selected airport.
 * Shows airline cards with navigation links to airline detail pages
 * and includes a customs contact section.
 */
const AirlineList: React.FC = () => {
  const location = useLocation();
  const { showSpin, hideSpin } = useSpin();
  const { unauthorize } = useUnAuthorize();
  const airport = (location.state as LocationState)?.airport;
  const [airlinesData, setAirlinesData] = useState<EnhancedAirline[]>([]);
  const fetchAirlinesList = useCallback(() => {
    showSpin();
    getAllAirlines()
      .then((res) => {
        const data = res?.data;
        if (res.is_success) {
          if (data && Array.isArray(data)) {
            const mapped = data.map((item: Airport) => ({
              ...item,
              img: airlineImageMap[item.iata_code] ?? null,
            }));

            setAirlinesData(mapped);
          }
        } else {
          setAirlinesData([]);
          message.error(res?.message || 'Failed to fetch airlines');
        }
      })
      .catch((error) => {
        unauthorize(error);
        setAirlinesData([]);
      })
      .finally(() => {
        hideSpin();
      });
  }, [unauthorize, showSpin, hideSpin]);

  useEffect(() => {
    fetchAirlinesList();
  }, [fetchAirlinesList]);

  const breadcrumbItems = [
    {
      title: 'Airports',
      href: '/airport-list',
    },
    {
      title: airport?.name ?? '',
    },
  ];

  return (
    <>
      <section className='airline-list'>
        <Flex vertical gap={16}>
          <Breadcrumbs items={breadcrumbItems} />
          <CustomTitle
            className='zl-custom-title'
            text={`${airport?.name}${airport?.iata_code?.trim() ? ` (${airport?.iata_code})` : ''}`}
            fontSize='24px'
            fontWeight='700'
            lineHeight='32px'
            color='var(--text-primary)'
            marginBottom='0'
          />
        </Flex>
        <Flex vertical gap={12} className='airline-data'>
          <CustomTitle
            text='Select from available airlines:'
            fontSize='12px'
            fontWeight='400'
            lineHeight='20px'
            color='var(--text-primary)'
            marginBottom='0'
          />
          <Row gutter={[16, 24]}>
            {airlinesData.map((airline: EnhancedAirline) => (
              <Col
                key={airline.name}
                xxl={6}
                xl={6}
                lg={12}
                md={12}
                sm={24}
                xs={24}
              >
                <Link
                  to={`/airport-list/airport/airline-detail/${airline.name
                    .replace(/\s+/g, '-')
                    .toLowerCase()}`}
                  state={{ airport, airline }}
                >
                  <Card className='airline-card'>
                    <CustomTitle
                      className='airline-name'
                      text={airline.name}
                      fontSize='18px'
                      fontWeight='700'
                      lineHeight='24px'
                      color='var(--text-primary)'
                      marginBottom='0'
                    />
                    {airline.img && (
                      <Image
                        className={`airline-img zl-custom-img${
                          darkInkLogos.has(airline.iata_code)
                            ? ' zl-invert-on-dark'
                            : ''
                        }`}
                        src={airline?.img}
                        preview={false}
                        alt={airline.name}
                      />
                    )}
                  </Card>
                </Link>
              </Col>
            ))}
            <Col xxl={6} xl={6} lg={12} md={12} sm={24} xs={24}>
              <CustomsContact
                airportName={airport?.name ?? ''}
                iataCode={airport?.iata_code ?? ''}
              />
            </Col>
          </Row>
        </Flex>
      </section>
    </>
  );
};

export default AirlineList;
